import type { DemoFilter, DemoRow, FilterOp } from './types';

/**
 * Filter parsing and matching, shared by the query engine and by realtime.
 *
 * Its own module, with no dependencies beyond types, for two reasons: one
 * grammar deserves one parser (postgres_changes filters use the same syntax as
 * query params, and two implementations of it would drift), and the realtime
 * shim is eager — `client.channel()` must return synchronously — so anything
 * it imports must stay small. The engine, store and generator remain lazy.
 */

const FILTER_OPS = new Set<string>([
  'eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'is', 'like', 'ilike',
]);

/** `eq.5` / `in.(a,b)` / `is.null` / `not.is.null` -> a DemoFilter. */
export function parseFilterValue(column: string, raw: string): DemoFilter | null {
  let rest = raw;
  let negated = false;
  if (rest.startsWith('not.')) {
    negated = true;
    rest = rest.slice(4);
  }

  const dot = rest.indexOf('.');
  if (dot === -1) return null;

  const op = rest.slice(0, dot);
  const rawValue = rest.slice(dot + 1);
  if (!FILTER_OPS.has(op)) return null;

  let value: unknown = rawValue;
  if (op === 'in') {
    value = rawValue
      .replace(/^\(/, '')
      .replace(/\)$/, '')
      .split(',')
      .filter(Boolean)
      .map((v) => v.replace(/^"(.*)"$/, '$1'));
  } else if (op === 'is') {
    value =
      rawValue === 'null' ? null : rawValue === 'true' ? true : rawValue === 'false' ? false : rawValue;
  }

  return { column, op: op as FilterOp, value, negated };
}

/** A realtime filter string, e.g. `id=eq.<uuid>`. */
export function parseFilterString(filter: string): DemoFilter | null {
  const eq = filter.indexOf('=');
  if (eq === -1) return null;
  return parseFilterValue(filter.slice(0, eq), filter.slice(eq + 1));
}

export function compare(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a) < String(b) ? -1 : 1;
}

export function matches(row: DemoRow, filter: DemoFilter): boolean {
  const actual = row[filter.column];
  const { op, value } = filter;

  let result: boolean;
  switch (op) {
    case 'eq':
      // Values arrive from a URL, so they are always strings. Compare loosely
      // rather than making every caller coerce.
      result = actual === value || String(actual) === String(value);
      break;
    case 'neq':
      result = String(actual) !== String(value);
      break;
    case 'in':
      result = Array.isArray(value) && value.some((v) => String(actual) === String(v));
      break;
    case 'is':
      result = value === null ? actual == null : actual === value;
      break;
    case 'gt':
      result = compare(actual, value) > 0;
      break;
    case 'gte':
      result = compare(actual, value) >= 0;
      break;
    case 'lt':
      result = compare(actual, value) < 0;
      break;
    case 'lte':
      result = compare(actual, value) <= 0;
      break;
    case 'like':
    case 'ilike': {
      const pattern = String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/%/g, '.*');
      result = new RegExp(`^${pattern}$`, op === 'ilike' ? 'i' : '').test(String(actual ?? ''));
      break;
    }
    default:
      result = true;
  }

  return filter.negated ? !result : result;
}
